import { useTranslation } from "react-i18next";
import { Button, Form, Layout, message, Spin, Tabs, TabsProps } from 'antd';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getSharePermissionDetail, saveSharePermission } from '../../service/learning-material';
import PageHeader from '../page-header/PageHeader';
import "./SharePermissionPage.scss";
import SharePermissionPersonalTab from './SharePermissionPersonalTab';
import SharePermissionPublicTab from './SharePermissionPublicTab';
import { SaveOutlined } from '@ant-design/icons';
import useKeyword from '../../hooks/useKeyword';
import { objectToQueryString } from '../../utils/getPageFromLink';

interface SharePermissionDetail {
	id: number;
	access: string;
	name: string;
	type: string;
	authors: {
		userId: number;
		permission: string;
	}[];
	universities: {
		enterpriseId: string;
		permission: number;
	}[];
	accessPermission: string
}

interface SavePermissionPayload {
	id: number;
	access: number | string;
	authors: {
		userId: number;
		permission: number;
	}[];
	universities: {
		enterpriseId: string;
		permission: number;
	}[];
	type: string | null;
	accessPermission: string
}

// Constants cho access levels
const ACCESS_LEVELS = {
	RESTRICTED: '1',    // Hạn chế
	PUBLIC: '2',        // Công khai
	UNIVERSITY: '3',    // Đơn vị đào tạo
};

// Constants cho permissions
const PERMISSION_LEVELS = {
	VIEW: '2',          // Xem
	EDIT: '3',          // Chỉnh sửa
};

const SharePermissionPage = () => {
  const { t } = useTranslation();
	const { shareId } = useParams();
	const type = useKeyword("type")
	const [detail, setDetail] = useState<SharePermissionDetail | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [isSaving, setIsSaving] = useState<boolean>(false);

	const [personalTabData, setPersonalTabData] = useState<any[]>([]);

	// publicTabData chứa tất cả thông tin về tab Nhóm
	const [publicTabData, setPublicTabData] = useState<{
		mode: string;
		universities: any[];
		publicPermission: string;
	}>({
		mode: ACCESS_LEVELS.RESTRICTED,
		universities: [],
		publicPermission: PERMISSION_LEVELS.VIEW
	});

	useEffect(() => {
		const fetchPermission = async () => {
			const params = {
				type: type
			}

			try {
				const res = await getSharePermissionDetail(Number(shareId), objectToQueryString(params));
				const data: SharePermissionDetail = res?.data?.data;
				setDetail(data);

				if (data) {
					let initialMode = ACCESS_LEVELS.RESTRICTED;
					if (data.access === ACCESS_LEVELS.PUBLIC) {
						initialMode = ACCESS_LEVELS.PUBLIC;
					} else if (data.access === ACCESS_LEVELS.UNIVERSITY && data.universities?.length > 0) {
						initialMode = ACCESS_LEVELS.UNIVERSITY;
					}
					setPublicTabData({
						mode: initialMode,
						universities: data.universities || [],
						publicPermission: data.accessPermission || PERMISSION_LEVELS.VIEW
					});
				}
			} catch (error) {
				console.error("Lỗi khi load chi tiết học liệu:", error);
			} finally {
				setIsLoading(false);
			}
		};

		fetchPermission();
	}, [shareId, type]);

	const handleSave = async () => {
		setIsSaving(true);
		try {
			let accessLevelNumber = 1;
			if (publicTabData.mode === ACCESS_LEVELS.PUBLIC) {
				accessLevelNumber = 2;
			} else if (publicTabData.mode === ACCESS_LEVELS.UNIVERSITY) {
				accessLevelNumber = 3;
			}

			const authors = personalTabData.map(user => ({
				userId: user.id,
				permission: parseInt(user.permission)
			}));

			let universities: any[] = [];
			if (publicTabData.mode === ACCESS_LEVELS.UNIVERSITY) {
				universities = publicTabData.universities.map(uni => ({
					enterpriseId: uni.enterpriseId || uni.id.toString(),
					permission: parseInt(uni.permission)
				}));
			}

			const payload: SavePermissionPayload = {
				id: parseInt(shareId!),
				access: String(accessLevelNumber),
				authors,
				universities,
				type,
				accessPermission: publicTabData.mode === ACCESS_LEVELS.PUBLIC ? publicTabData.publicPermission : "",
			};

			await saveSharePermission(payload);
			message.success(t("Lưu chia sẻ quyền truy cập thành công!", "Lưu chia sẻ quyền truy cập thành công!"));
		} catch (error) {
			console.error('Error saving permissions:', error);
			message.error(t("Có lỗi xảy ra khi lưu chia sẻ quyền truy cập!", "Có lỗi xảy ra khi lưu chia sẻ quyền truy cập!"));
		} finally {
			setIsSaving(false);
		}
	};

	const tabs: TabsProps["items"] = [
		{
			key: '1',
			label: t("Cá nhân", "Cá nhân"),
			children: (
				<div className="share-permission-tab">
					<SharePermissionPersonalTab
						authorizedLecturerList={detail?.authors || []}
						onDataChange={setPersonalTabData}
					/>
				</div>
			)
		},
		{
			key: '2',
			label: t("Nhóm", "Nhóm"),
			children: (
				<div className="share-permission-tab">
					<SharePermissionPublicTab
						universitiesList={detail?.universities}
						initialMode={detail?.access || "1"}
						initialPublicPermission={detail?.accessPermission}
						onDataChange={setPublicTabData}
					/>
				</div>
			)
		}
	];

	if (isLoading) {
		return (
			<Layout className="share-permission-loading">
				<div style={{ textAlign: 'center', padding: '50px' }}>
					<Spin />
				</div>
			</Layout>
		);
	}

	return (
		<Layout className="page-header-group share-permission-page">
			<Form.Provider>
				<div className="page-header-group--layout">
					<div className="table-header--wrapper justify-between">
						<PageHeader title={detail?.name || t("Chia sẻ quyền truy cập", "Chia sẻ quyền truy cập")} />
						<Button
							type="primary"
							icon={<SaveOutlined />}
							onClick={handleSave}
							loading={isSaving}
						>
							{t("Lưu", "Lưu")}
						</Button>
					</div>
				</div>

				<Tabs defaultActiveKey="1" items={tabs} />
			</Form.Provider>
		</Layout>
	);
};

export default SharePermissionPage;